import { useQuery } from '@tanstack/react-query'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { useMemo, useState } from 'react'
import { BranchDetailSheet } from '@/components/branch-detail-sheet'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { usePageTitle } from '@/hooks/use-page-title'
import { adminApi, type AdminBranch } from '@/lib/admin-api'
import { cn } from '@/utils/misc'

type SortKey =
	| 'branchId'
	| 'branchName'
	| 'ico'
	| 'departmentId'
	| 'fb_ipaddress'
	| 'setupAt'
	| 'createdAt'

type SortState = { key: SortKey; direction: 'asc' | 'desc' }

const COLUMNS: { key: SortKey; label: string }[] = [
	{ key: 'branchId', label: 'Branch ID' },
	{ key: 'branchName', label: 'Name' },
	{ key: 'ico', label: 'IČO' },
	{ key: 'departmentId', label: 'Department' },
	{ key: 'fb_ipaddress', label: 'IP address' },
	{ key: 'setupAt', label: 'Status' },
	{ key: 'createdAt', label: 'Created' },
]

export function AdminBranchesPage() {
	usePageTitle('Branches')

	const branchesQuery = useQuery({
		queryKey: ['admin', 'branches'],
		queryFn: adminApi.branches,
	})

	const [sort, setSort] = useState<SortState>({
		key: 'branchId',
		direction: 'asc',
	})

	const [selectedBranchId, setSelectedBranchId] = useState<number | null>(null)

	const sortedBranches = useMemo(() => {
		if (!branchesQuery.data) return []
		return [...branchesQuery.data].sort((a, b) =>
			compareBranches(a, b, sort),
		)
	}, [branchesQuery.data, sort])

	const toggleSort = (key: SortKey) => {
		setSort((prev) =>
			prev.key === key
				? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
				: { key, direction: 'asc' },
		)
	}

	return (
		<>
		<div className="space-y-6">
			<header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
				<div>
					<h1 className="text-2xl font-bold tracking-tight text-gray-900">
						Branches
					</h1>
					<p className="text-sm text-gray-500 mt-1">
						Browse all branches and their connection status
					</p>
				</div>
				{branchesQuery.data && (
					<span className="text-sm text-gray-500">
						{branchesQuery.data.length} total
					</span>
				)}
			</header>

			<div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
				<Table>
					<TableHeader>
						<TableRow className="hover:bg-transparent border-b border-gray-200">
							{COLUMNS.map((col) => {
								const active = sort.key === col.key
								return (
									<TableHead
										key={col.key}
										className="whitespace-nowrap h-11 px-4 bg-gray-50 text-xs font-semibold uppercase tracking-wider text-gray-500 border-r border-gray-100 last:border-r-0"
									>
										<button
											type="button"
											onClick={() => toggleSort(col.key)}
											className={cn(
												'inline-flex items-center gap-1.5 transition-colors -mx-2 px-2 py-1 rounded hover:text-gray-900',
												active && 'text-gray-900',
											)}
										>
											<span>{col.label}</span>
											<SortIcon active={active} direction={sort.direction} />
										</button>
									</TableHead>
								)
							})}
						</TableRow>
					</TableHeader>
					<TableBody>
						{branchesQuery.isPending ? (
							<>
								{Array.from({ length: 6 }).map((_, i) => (
									// biome-ignore lint/suspicious/noArrayIndexKey: placeholder rows
									<TableRow key={i}>
										{COLUMNS.map((col) => (
											<TableCell key={col.key}>
												<Skeleton className="h-4 w-20" />
											</TableCell>
										))}
									</TableRow>
								))}
							</>
						) : branchesQuery.isError ? (
							<TableRow>
								<TableCell
									colSpan={COLUMNS.length}
									className="text-center py-10 text-red-700"
								>
									Could not load branches.
								</TableCell>
							</TableRow>
						) : sortedBranches.length === 0 ? (
							<TableRow>
								<TableCell
									colSpan={COLUMNS.length}
									className="text-center py-10 text-gray-500"
								>
									No branches yet.
								</TableCell>
							</TableRow>
						) : (
							sortedBranches.map((b) => (
								<BranchRow
									key={b.id}
									branch={b}
									onClick={() => setSelectedBranchId(b.branchId)}
								/>
							))
						)}
					</TableBody>
				</Table>
			</div>
		</div>
		<BranchDetailSheet
			branchId={selectedBranchId}
			onClose={() => setSelectedBranchId(null)}
		/>
		</>
	)
}

function BranchRow({ branch, onClick }: { branch: AdminBranch; onClick: () => void }) {
	const connected = Boolean(branch.setupAt)
	return (
		<TableRow
			className="cursor-pointer hover:bg-gray-50"
			onClick={onClick}
		>
			<TableCell className="font-mono text-sm text-gray-900">
				{branch.branchId}
			</TableCell>
			<TableCell className="text-gray-900">
				{branch.branchName || <span className="text-gray-400">—</span>}
			</TableCell>
			<TableCell className="font-mono text-sm text-gray-600">
				{branch.ico}
			</TableCell>
			<TableCell className="text-gray-600">
				{branch.departmentId ?? <span className="text-gray-400">—</span>}
			</TableCell>
			<TableCell className="font-mono text-sm text-gray-600">
				{branch.fb_ipaddress || <span className="text-gray-400">—</span>}
			</TableCell>
			<TableCell>
				<span
					className={cn(
						'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium',
						connected
							? 'bg-green-50 text-green-700'
							: 'bg-amber-50 text-amber-700',
					)}
				>
					<span
						className={cn(
							'h-1.5 w-1.5 rounded-full',
							connected ? 'bg-green-500' : 'bg-amber-500',
						)}
					/>
					{connected ? 'Connected' : 'Pending'}
				</span>
			</TableCell>
			<TableCell className="text-gray-600 text-sm whitespace-nowrap">
				{new Date(branch.createdAt).toLocaleDateString(undefined, {
					day: 'numeric',
					month: 'short',
					year: 'numeric',
				})}
			</TableCell>
		</TableRow>
	)
}

function SortIcon({
	active,
	direction,
}: {
	active: boolean
	direction: 'asc' | 'desc'
}) {
	if (!active) {
		return <ArrowUpDown className="h-3.5 w-3.5 text-gray-400" />
	}
	return direction === 'asc' ? (
		<ArrowUp className="h-3.5 w-3.5 text-red-600" />
	) : (
		<ArrowDown className="h-3.5 w-3.5 text-red-600" />
	)
}

function compareBranches(
	a: AdminBranch,
	b: AdminBranch,
	sort: SortState,
): number {
	const mul = sort.direction === 'asc' ? 1 : -1
	const av = a[sort.key]
	const bv = b[sort.key]

	// Nulls sink to the bottom regardless of direction.
	if (av == null && bv == null) return 0
	if (av == null) return 1
	if (bv == null) return -1

	if (typeof av === 'number' && typeof bv === 'number') {
		return (av - bv) * mul
	}

	if (sort.key === 'setupAt' || sort.key === 'createdAt') {
		return (Date.parse(String(av)) - Date.parse(String(bv))) * mul
	}

	return String(av).localeCompare(String(bv)) * mul
}
